import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Download, FileText } from './Icons'
import type { GamesRegistry, GameConfig, GameMarket } from '@/types/content'

interface GamesRegistryPageProps {
  backUrl?: string
}

export default function GamesRegistryPage({ backUrl }: GamesRegistryPageProps) {
  const [data, setData] = useState<GamesRegistry | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetch(`/api/games-registry.json?v=${__COMMIT_HASH__}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(jsonData => {
        setData(jsonData)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const toggleGame = (gameId: string) => {
    setExpandedGames(prev => {
      const newSet = new Set(prev)
      if (newSet.has(gameId)) {
        newSet.delete(gameId)
      } else {
        newSet.add(gameId)
      }
      return newSet
    })
  }

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  const formatMoney = (num: number): string => {
    return `${formatNumber(num)} ₽`
  }

  const formatPercent = (num: number): string => {
    return `${(num * 100).toFixed(2)}%`
  }

  const formatDecimal = (num: number): string => {
    return num.toFixed(2)
  }

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <div className="games-registry-page min-h-screen bg-dark text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-400">Загрузка реестра игровых конфигураций...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="games-registry-page min-h-screen bg-dark text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Ошибка загрузки данных: {error || 'Неизвестная ошибка'}</p>
          {backUrl && (
            <a href={backUrl} className="text-primary hover:text-primary-light">
              Вернуться на главную
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="games-registry-page min-h-screen bg-dark text-white">
      {/* Header */}
      <div className="bg-dark-secondary border-b border-primary/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            {backUrl ? (
              <a
                href={backUrl}
                className="inline-flex items-center text-sm text-gray-400 hover:text-primary transition-colors"
              >
                <ChevronDown className="w-4 h-4 mr-1 rotate-90" />
                Вернуться на главную
              </a>
            ) : (
              <div />
            )}
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Сохранить как PDF
            </button>
          </div>
          <div className="flex items-start space-x-4">
            <FileText className="w-12 h-12 text-primary flex-shrink-0" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{data.meta.documentType}</h1>
              <div className="text-sm text-gray-400 space-y-1">
                <p>Дата формирования: {new Date(data.meta.generatedAt).toLocaleString('ru-RU')}</p>
                <p>Версия: {data.meta.version}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-dark-secondary border border-primary/20 rounded-lg p-6">
            <div className="text-3xl font-bold text-primary mb-1">{data.summary.totalGames}</div>
            <div className="text-sm text-gray-400">Всего игр</div>
          </div>
          <div className="bg-dark-secondary border border-primary/20 rounded-lg p-6">
            <div className="text-3xl font-bold text-primary mb-1">{data.summary.totalMarkets}</div>
            <div className="text-sm text-gray-400">Всего рынков</div>
          </div>
          <div className="bg-dark-secondary border border-primary/20 rounded-lg p-6">
            <div className="text-3xl font-bold text-primary mb-1">
              {formatPercent(data.summary.rtpRange.min)} - {formatPercent(data.summary.rtpRange.max)}
            </div>
            <div className="text-sm text-gray-400">Диапазон RTP</div>
          </div>
          <div className="bg-dark-secondary border border-primary/20 rounded-lg p-6">
            <div className="text-3xl font-bold text-primary mb-1">
              {formatDecimal(data.summary.factorRange.min)} - {formatDecimal(data.summary.factorRange.max)}
            </div>
            <div className="text-sm text-gray-400">Диапазон коэффициентов</div>
          </div>
        </div>

        {/* Games List */}
        <div className="space-y-4">
          {data.games.map((game) => (
            <GameCard
              key={game.gameId}
              game={game}
              isExpanded={expandedGames.has(game.gameId)}
              onToggle={() => toggleGame(game.gameId)}
              formatMoney={formatMoney}
              formatPercent={formatPercent}
              formatDecimal={formatDecimal}
              formatNumber={formatNumber}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface GameCardProps {
  game: GameConfig
  isExpanded: boolean
  onToggle: () => void
  formatMoney: (num: number) => string
  formatPercent: (num: number) => string
  formatDecimal: (num: number) => string
  formatNumber: (num: number) => string
}

function GameCard({ game, isExpanded, onToggle, formatMoney, formatPercent, formatDecimal, formatNumber }: GameCardProps) {
  return (
    <div className="bg-dark-secondary border border-primary/20 rounded-lg overflow-hidden">
      {/* Game Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-dark-tertiary transition-colors"
      >
        <div className="text-left">
          <h3 className="text-xl font-bold text-primary mb-1">{game.gameName}</h3>
          <p className="text-sm text-gray-400">
            Тип: <span className="text-white">{game.gameType}</span>
            {game.totalOutcomes && (
              <>
                {' '}| Всего исходов: <span className="text-white">{formatNumber(game.totalOutcomes)}</span>
              </>
            )}
          </p>
          <p className="text-sm text-gray-300 mt-1">{game.description}</p>
        </div>
        <ChevronDown
          className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Game Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-6">
              {/* Bet Limits */}
              <div className="bg-dark-tertiary border border-primary/10 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-primary mb-3">Лимиты пари</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Минимальное пари:</span>{' '}
                    <span className="text-white font-medium">{formatMoney(game.betLimits.minBet)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Максимальное общее пари:</span>{' '}
                    <span className="text-white font-medium">{formatMoney(game.betLimits.maxTotalBet)}</span>
                  </div>
                  {game.betLimits.maxWinAmount && (
                    <div>
                      <span className="text-gray-400">Максимальный выигрыш:</span>{' '}
                      <span className="text-white font-medium">{formatMoney(game.betLimits.maxWinAmount)}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-400">Доступные размеры пари:</span>{' '}
                    <span className="text-white font-medium">
                      {game.betLimits.availableBetSizes.map(formatMoney).join(', ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Market Counts (Dice specific) */}
              {game.marketCounts && (
                <div className="bg-dark-tertiary border border-primary/10 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-primary mb-3">Количество рынков по типам</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                    {Object.entries(game.marketCounts).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-gray-400">{key}:</span>{' '}
                        <span className="text-white font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Presets (Bingo, Mafia, Zoloto) */}
              {game.presets && game.presets.length > 0 && (
                <div className="bg-dark-tertiary border border-primary/10 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-primary mb-3">Пресеты</h4>
                  <div className="space-y-3">
                    {game.presets.map((preset) => (
                      <div key={preset.presetId} className="text-sm border-l-2 border-primary/30 pl-3">
                        <div className="font-medium text-white">{preset.presetName}</div>
                        <div className="text-gray-400 text-xs">{preset.description}</div>
                        <div className="mt-1 text-xs text-gray-500">
                          Ячеек: {preset.cells}
                          {preset.diagonalFactor && ` | Диагональный коэф.: ${preset.diagonalFactor}`}
                          {preset.regularFactor && ` | Обычный коэф.: ${preset.regularFactor}`}
                          {preset.factors && ` | Коэффициенты: ${preset.factors.join(', ')}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Markets Table */}
              <div>
                <h4 className="text-sm font-semibold text-primary mb-3">
                  Рынки ({game.markets.length})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Название рынка</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Коэф. (raw)</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Коэф. (decimal)</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Выигрышные исходы</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Вероятность</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">RTP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {game.markets.map((market) => (
                        <MarketRow
                          key={market.marketId}
                          market={market}
                          formatPercent={formatPercent}
                          formatDecimal={formatDecimal}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface MarketRowProps {
  market: GameMarket
  formatPercent: (num: number) => string
  formatDecimal: (num: number) => string
}

function MarketRow({ market, formatPercent, formatDecimal }: MarketRowProps) {
  // Handle factor ranges (Fishing game)
  const factorDisplay = market.factorRange
    ? `${market.factorRange.min} - ${market.factorRange.max}`
    : market.factor !== null && market.factor !== undefined
    ? market.factor.toString()
    : market.factorType === 'dynamic'
    ? 'Динамический'
    : '-'

  const factorDecimalDisplay = market.factorDecimalRange
    ? `${formatDecimal(market.factorDecimalRange.min)} - ${formatDecimal(market.factorDecimalRange.max)}`
    : market.factorDecimal !== null && market.factorDecimal !== undefined
    ? formatDecimal(market.factorDecimal)
    : market.factorType === 'dynamic'
    ? 'Динамический'
    : '-'

  return (
    <tr className="border-b border-primary/10 hover:bg-dark-tertiary/50">
      <td className="py-3 px-3">
        <div className="text-white font-medium">{market.marketName}</div>
        {market.description && <div className="text-xs text-gray-500 mt-0.5">{market.description}</div>}
        {market.presetType && (
          <div className="text-xs text-primary/70 mt-0.5">Тип пресета: {market.presetType}</div>
        )}
        {market.note && <div className="text-xs text-yellow-500/70 mt-0.5">Примечание: {market.note}</div>}
        {market.activeSlots && (
          <div className="text-xs text-gray-500 mt-0.5">Активных слотов: {market.activeSlots}</div>
        )}
      </td>
      <td className="py-3 px-3 text-white">{factorDisplay}</td>
      <td className="py-3 px-3 text-white">{factorDecimalDisplay}</td>
      <td className="py-3 px-3 text-white">
        {market.winningOutcomes !== undefined && market.totalOutcomes !== undefined
          ? `${market.winningOutcomes} / ${market.totalOutcomes}`
          : '-'}
      </td>
      <td className="py-3 px-3 text-white">
        {market.probability !== undefined ? formatPercent(market.probability) : '-'}
      </td>
      <td className="py-3 px-3 text-white">
        {market.rtp !== null && market.rtp !== undefined ? formatPercent(market.rtp) : '-'}
      </td>
    </tr>
  )
}
